import type { ReactNode } from "react";
import { InformationCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { useDismissibleHint } from "@/shared/hooks/useDismissibleHint";

interface DismissibleTipProps {
  /** Storage key suffix. Each tip is dismissed on its own. */
  id: string;
  children: ReactNode;
  className?: string;
}

/**
 * A one-off explanation of a rule that isn't visible in the UI itself.
 *
 * Extracted from the query pane once there were three of these: identical
 * markup copied three times is how a set of tips drifts into three slightly
 * different boxes, and a tip that looks improvised reads as a warning — which
 * is the failure this pattern already went through once.
 *
 * Dismissal is per-tip and per-browser, so answering one leaves the others,
 * and none of them come back.
 */
export default function DismissibleTip({
  id,
  children,
  className,
}: DismissibleTipProps) {
  const { dismissed, dismiss } = useDismissibleHint(id);
  if (dismissed) return null;

  return (
    <div
      className={clsx(
        "flex items-start gap-2 rounded border border-gray-700 bg-gray-800/70 px-2.5 py-2",
        className
      )}
    >
      <InformationCircleIcon className="mt-px size-4 shrink-0 text-gray-500" />
      <p className="flex-1 text-xs leading-relaxed text-gray-300">{children}</p>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Got it"
        className="shrink-0 cursor-pointer border-none bg-transparent p-0.5 text-gray-500 transition-colors hover:text-gray-300"
      >
        <XMarkIcon className="size-3.5" />
      </button>
    </div>
  );
}
